import { useTranslation } from 'react-i18next'

import { FancyLoader } from '../FancyLoader'
import { Pagination } from '../Pagination'

import type { GenericTableProps } from './Table.props'

export function Table<T extends { [key: string]: unknown }>({
  data,
  columns,
  emptyMessage = 'No data available',
  pagination,
  isLoading = false,
  initial,
  setPagination,
  onRowAction,
  title,
}: GenericTableProps<T>) {
  const { t } = useTranslation()

  const handleRowAction = (
    row: T,
    rowIndex: number,
    event: React.MouseEvent<HTMLTableRowElement>
  ) => {
    if (onRowAction) {
      onRowAction(row, rowIndex, event)
    }
  }

  return (
    <div className="flex flex-col w-full relative my-8">
      <div className="w-full overflow-x-auto">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
            <FancyLoader />
          </div>
        )}
        {!isLoading && !initial && (!data || data.length === 0) && (
          <div className="flex flex-col p-6">
            <div className="text-center py-8 text-gray-500">
              {String(t(emptyMessage))}
            </div>
          </div>
        )}
        {!isLoading && data && data.length > 0 && (
          <div className="overflow-x-auto px-4">
            <table className="table-auto w-full text-sm">
              {title && (
                <caption className="text-left font-bold text-lg mb-4">
                  {t(title)}
                </caption>
              )}
              <thead>
                <tr className="bg-gray-100">
                  {columns.map((col, idx) => (
                    <th key={idx} className="px-6 py-3 text-left">
                      {String(t(col.header))}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={(event) => handleRowAction(row, rowIndex, event)}
                  >
                    {columns.map((col, colIndex) => (
                      <td key={colIndex} className="px-6 py-3 text-left">
                        {col.render
                          ? col.render(row)
                          : col.accessor
                            ? String(row[col.accessor])
                            : null}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {!isLoading && data && data.length > 0 && (
        <Pagination pagination={pagination} setPagination={setPagination} />
      )}
    </div>
  )
}

export default Table
